import _ from 'lodash';

import { assertNever } from '@tock/shared/utils/typeUtils';
import { consumer, toJSON } from '@tock/coretypes';
import AppConstants from 'constants/AppConstants';

interface State {
  isInitialized: boolean;
  requestInProgress: boolean;
  consumerPage: {
    [slug: string]: consumer.ConsumerPage | null;
  };
}

const initialState = {
  isInitialized: false,
  consumerPage: {
    '/': null,
  },
  requestInProgress: false,
};

export default function reducer(state: State = initialState, action: BaseTockAction): State {
  const { type, payload } = action;
  switch (type) {
    case AppConstants.CONSUMER_PAGE_SEARCH_INIT: {
      return {
        ...state,
        isInitialized: true,
      };
    }
    case AppConstants.CONSUMER_PAGE_SEARCH_STARTING: {
      return {
        ...state,
        requestInProgress: true,
      };
    }
    case AppConstants.CONSUMER_PAGE_SEARCH_DONE: {
      if (payload.variant.length === 0) {
        return {
          ...state,
          isInitialized: true,
          requestInProgress: false,
        };
      }
      const { request } = action;
      return {
        ...state,
        consumerPage: {
          ...state.consumerPage,
          [request.slug]: toJSON(payload),
        },
        isInitialized: true,
        requestInProgress: false,
      };
    }
    case AppConstants.CONSUMER_PAGE_SEARCH_FAILED: {
      return {
        ...state,
        isInitialized: true,
        requestInProgress: false,
      };
    }
    case AppConstants.CONSUMER_PAGE_WIDGET_DATA_FETCH_DONE: {
      const { request } = action;
      const response = toJSON<consumer.WidgetContent>(payload);
      const specificPage = _.cloneDeep(state.consumerPage[request.slug]);
      if (!specificPage) {
        return { ...state };
      }
      const specificPageTemplate = specificPage.variant[0].template;
      if (!specificPageTemplate || !specificPageTemplate.widget) {
        return { ...state };
      }
      _.forEach(specificPageTemplate.widget, (oldWidget: consumer.Widget) => {
        if (_.toInteger(oldWidget?.widgetInfo?.id) === parseInt(request.widgetId, 10)) {
          oldWidget.content = {
            ...oldWidget.content,
            business: [...(oldWidget.content?.business || []), ...response.business],
            offering: [...(oldWidget.content?.offering || []), ...response.offering],
            metroArea: [...(oldWidget.content?.metroArea || []), ...response.metroArea],
            shopBrandAvailability: [
              ...(oldWidget.content?.shopBrandAvailability || []),
              ...response.shopBrandAvailability,
            ],
            shopItemAvailability: [
              ...(oldWidget.content?.shopItemAvailability || []),
              ...response.shopItemAvailability,
            ],
            shopWineRegion: [
              ...(oldWidget.content?.shopWineRegion || []),
              ...response.shopWineRegion,
            ],
          };
        }
      });
      return {
        ...state,
        consumerPage: {
          ...state.consumerPage,
          [request.slug]: specificPage,
        },
      };
    }
    case AppConstants.CONSUMER_PAGE_WIDGET_DATA_FETCH_V2_DONE: {
      const { request } = action;

      const response = toJSON<consumer.Widget>(payload);
      const specificPage = _.cloneDeep(state.consumerPage[request.slug]);
      if (!specificPage) {
        return { ...state };
      }
      const specificPageTemplate = specificPage.variant[0].template;
      if (!specificPageTemplate || !specificPageTemplate.widget) {
        return { ...state };
      }

      const match = specificPageTemplate.widget.find((w) => w.widgetInfo?.id === request.widgetId);
      const dataType = (response.gridWidget as consumer.GridWidget).data;

      if (match) {
        const getGridWidget = () => {
          if (!dataType) {
            return match?.gridWidget;
          }
          switch (dataType) {
            case 'businessData':
              return {
                ...match.gridWidget,
                businessData: {
                  business: [
                    ...(match.gridWidget?.businessData?.business ?? []),
                    ...(response.gridWidget?.businessData?.business ?? []),
                  ],
                },
              };
            case 'experienceData':
              return {
                ...match.gridWidget,
                experienceData: {
                  experience: [
                    ...(match.gridWidget?.experienceData?.experience ?? []),
                    ...(response.gridWidget?.experienceData?.experience ?? []),
                  ],
                },
              };

            default:
              assertNever(dataType);
          }
        };

        switch (response.widget) {
          case 'gridWidget': {
            match.gridWidget = getGridWidget();
          }
        }
      }

      return {
        ...state,
        consumerPage: {
          ...state.consumerPage,
          [request.slug]: specificPage,
        },
      };
    }
    default: {
      return {
        ...state,
      };
    }
  }
}
